import {
  DefinedUseQueryResult,
  skipToken,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useContext, useMemo } from "react";
import SitesContext, {
  useOptionalSiteIdParam,
} from "../../context/SitesContext";

import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import { VITE_SITE_SERVICE_BASE_URL } from "@src/env";
import { Building, SummaryOverview } from "@src/types";
import { parse } from "papaparse";
import { useCurrentUser } from "../../context/UserContext";
import FetchClients from "../backpackSdk/client";
import {
  BackpackServiceError,
  isBackpackResponseErrorBody,
  throwBackpackSuperagentResponseError,
} from "../backpackSdk/errors";
import {
  ACCOUNT_SITES_KEY,
  SITE_ACCOUNTS_KEY,
} from "../backpackSdk/sites/siteAccounts";
import { retryIfNot404 } from "../fortyTwoApi/reactQueryUtils";
import { createNewSite, getSite, getSites, updateSite } from "../postgrestApi";
import request from "../request";
import { SITE_SUMMARY_FIELDS } from "./sitesHelpers";

const useIsInactiveSitesEnabled = () => {
  const isInactiveSitesEnabled =
    useFeatureFlag("inactive-sites", "off") === "on";
  const { superAdmin } = useCurrentUser();
  return superAdmin && isInactiveSitesEnabled;
};

export const SITE_SUMMARY_KEY = "summary-overview";

export function useSiteSummaryQueryKey({
  includeInactiveSites,
  accountId,
}: {
  includeInactiveSites?: boolean;
  accountId?: number;
}) {
  return useMemo(
    () => [SITE_SUMMARY_KEY, accountId, includeInactiveSites],
    [accountId, includeInactiveSites]
  );
}

export function useSitesSummaryQuery({
  enabled,
  includeInactiveSites,
  accountId,
}: {
  enabled?: boolean;
  includeInactiveSites?: boolean;
  accountId?: number; // Only superadmin views should omit this parameter
}) {
  const queryKey = useSiteSummaryQueryKey({ includeInactiveSites, accountId });
  return useQuery({
    queryKey: enabled ? queryKey : ["disabled-query"],
    queryFn: enabled
      ? () =>
          getSites({
            includeInactive: includeInactiveSites,
            fields: SITE_SUMMARY_FIELDS,
            accountId,
          })
      : skipToken,
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 60000,
  });
}

export function useSitesSummaryBySiteIdQuery({
  siteIds,
}: {
  siteIds: number[];
}) {
  const results = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["site", siteId],
      queryFn: () => getSite(siteId, { fields: SITE_SUMMARY_FIELDS }),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 60000,
    })),
  });

  return useMemo(
    () => ({
      data: results.every(
        (r): r is DefinedUseQueryResult<SummaryOverview> =>
          typeof r.data !== "undefined" && r.data !== null
      )
        ? results.flatMap((r) => r.data)
        : undefined,
      isPending: results.some((r) => r.isPending),
      isError: results.some((r) => r.isError),
      isSuccess: results.every((r) => r.isSuccess),
    }),
    [results]
  );
}

export const DETAILED_SITE_SUMMARY_KEY = "site-summary";

export const useDetailedSiteSummaryQuery = ({
  siteId,
  throwErrors = false,
}: { siteId?: string | number | null; throwErrors?: boolean } = {}) => {
  const _siteId = useOptionalSiteIdParam();
  const includeInactive = useIsInactiveSitesEnabled();
  const id = siteId ?? _siteId;
  return useQuery({
    queryKey: [
      DETAILED_SITE_SUMMARY_KEY,
      String(id),
      (!!includeInactive).toString(),
    ],
    queryFn: () => getSite(id!, { includeInactive, throwErrors }),
    enabled: !!id && includeInactive !== undefined,
    refetchOnWindowFocus: false,
    retry: retryIfNot404,
  });
};

interface DetailedSitesSummaryOptions {
  includeInactive?: boolean;
  accountId?: number; // Request is not enabled if accountId is omitted. Detailed sites summary request must always be filtered to a single account.
}
export const useDetailedSitesSummaryQuery = ({
  includeInactive,
  accountId,
}: DetailedSitesSummaryOptions) => {
  const isInactiveSitesEnabled = useIsInactiveSitesEnabled();
  const _includeInactive = includeInactive ?? isInactiveSitesEnabled;
  const enabled = _includeInactive !== undefined && accountId !== undefined;

  return useQuery({
    queryKey: [
      DETAILED_SITE_SUMMARY_KEY,
      "all",
      (!!_includeInactive).toString(),
      accountId,
    ],
    queryFn: enabled
      ? () => getSites({ includeInactive: _includeInactive, accountId })
      : skipToken,
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });
};

const noop = () => {
  // intentional noop, expect error to be caught by bugsnag previously
};

export const useInvalidateAllSiteQueries = () => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [SITE_SUMMARY_KEY],
      }),
      queryClient.invalidateQueries({
        queryKey: [DETAILED_SITE_SUMMARY_KEY],
      }),
    ]);
  }, [queryClient]);
};

export const useInvalidateSiteQueries = ({
  siteId,
}: { siteId?: string | number | null } = {}) => {
  const queryClient = useQueryClient();
  const { refreshSite } = useContext(SitesContext);
  const _siteId = useOptionalSiteIdParam();
  const id = siteId ?? _siteId;
  const refreshSiteQueries = useCallback(() => {
    // errors already caught by bugnsag in api
    // refreshSite/refreshSiteQueries is generally not awaited
    // so adding .catches here to appease sonar
    // https://sonarcloud.io/organizations/backpack/rules?open=typescript%3AS6544&rule_key=typescript%3AS6544
    if (id) {
      queryClient
        .invalidateQueries(
          { queryKey: [DETAILED_SITE_SUMMARY_KEY, String(id)] },
          { throwOnError: true }
        )
        .catch(noop);
      refreshSite(id).catch(noop);
    }
    queryClient
      .invalidateQueries(
        { queryKey: ["site-summary", "all"] },
        { throwOnError: true }
      )
      .catch(noop);
  }, [id, queryClient, refreshSite]);
  const refreshSiteQueriesAsync = useCallback(() => {
    if (id) {
      return Promise.all([
        queryClient.invalidateQueries(
          { queryKey: [DETAILED_SITE_SUMMARY_KEY, String(id)] },
          { throwOnError: true }
        ),
        queryClient.invalidateQueries({
          queryKey: [DETAILED_SITE_SUMMARY_KEY, "all"],
        }),
        refreshSite(id),
      ]);
    }
    return queryClient.invalidateQueries({
      queryKey: [DETAILED_SITE_SUMMARY_KEY, "all"],
    });
  }, [id, queryClient, refreshSite]);
  return {
    refreshSiteQueries,
    refreshSiteQueriesAsync,
  };
};

export const usePostSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newSite: Partial<Building>) => createNewSite(newSite),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: [SITE_SUMMARY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_SITES_KEY, accountId],
      });
    },
  });
};

const IS_TEST_SITE_CSV_FIELD = "Is Test Site";
const IS_TEST_SITE_TRUE_VALUES = new Set(["yes", "true", "y", "1", "required"]);
const IS_TEST_SITE_FALSE_VALUES = new Set(["no", "false", "n", "0", ""]);

function normalizeCsvHeader(header: string) {
  return header.trim().toLowerCase();
}

function isValidIsTestSiteCsvValue(value: string) {
  const normalized = value.trim().toLowerCase();
  return (
    IS_TEST_SITE_TRUE_VALUES.has(normalized) ||
    IS_TEST_SITE_FALSE_VALUES.has(normalized)
  );
}

function findIsTestSiteCsvField(fields: string[] | undefined) {
  const normalizedExpected = normalizeCsvHeader(IS_TEST_SITE_CSV_FIELD);
  return fields?.find(
    (field) => normalizeCsvHeader(field) === normalizedExpected
  );
}

function assertValidIsTestSiteCsvColumn(
  rows: unknown[],
  fields: string[] | undefined
) {
  const isTestSiteField = findIsTestSiteCsvField(fields);
  if (!isTestSiteField) return;

  const invalidRows: string[] = [];

  rows.forEach((row, index) => {
    if (!row || typeof row !== "object") return;

    const rowRecord = row as Record<string, unknown>; // papaparse row objects are keyed by CSV headers
    if (!(isTestSiteField in rowRecord)) return;

    const rawValue = rowRecord[isTestSiteField];
    const value = rawValue == null ? "" : String(rawValue);
    if (!isValidIsTestSiteCsvValue(value)) {
      // +2: header is spreadsheet row 1, first data row is row 2
      invalidRows.push(`row ${index + 2}: "${value}"`);
    }
  });

  if (invalidRows.length > 0) {
    throw new Error(
      `Invalid value for "${IS_TEST_SITE_CSV_FIELD}" on ${invalidRows.join(
        "; "
      )}. Accepted values are: yes, true, y, 1, required, no, false, n, 0, or empty.`
    );
  }
}

export function usePostBulkSitesCsvUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (csv: File) => {
      await new Promise<void>((resolve, reject) => {
        parse(csv, {
          header: true,
          complete: (results) => {
            try {
              assertValidIsTestSiteCsvColumn(results.data, results.meta.fields);
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          error: reject,
        });
      });

      try {
        await request
          .post(`${VITE_SITE_SERVICE_BASE_URL}/sites/bulk-with-accounts`)
          .send(csv);
      } catch (error) {
        throwBackpackSuperagentResponseError(error);
      }
    },
    onSuccess: () => {
      return Promise.all([
        // we can upload sites to any number of accounts, so we need to invalidate the queries sites for all accounts
        queryClient.invalidateQueries({ queryKey: [SITE_SUMMARY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [ACCOUNT_SITES_KEY] }),
        queryClient.invalidateQueries({ queryKey: [SITE_ACCOUNTS_KEY] }),
      ]);
    },
  });
}

export const usePatchSite = () => {
  const queryClient = useQueryClient();
  const { refreshSiteQueriesAsync } = useInvalidateSiteQueries();

  return useMutation({
    mutationFn: (building: { id: number & Partial<Building> }) =>
      updateSite(building.id, building),
    onSuccess: async (_, { id }) => {
      await queryClient.invalidateQueries({ queryKey: [SITE_SUMMARY_KEY] });
      await queryClient.invalidateQueries({
        queryKey: [ACCOUNT_SITES_KEY],
      });
      await refreshSiteQueriesAsync();
      await queryClient.invalidateQueries({
        queryKey: [DETAILED_SITE_SUMMARY_KEY, String(id)],
      });
    },
  });
};

// Called only by super admin, so feature access does not need to be checked
const deleteSite = async (siteId: string | number) => {
  const { response, error } = await FetchClients.site.PATCH("/{id}", {
    params: { path: { id: Number(siteId) } },
    body: { deleted_at: new Date().toISOString() },
  });
  if (response.ok) return;
  if (isBackpackResponseErrorBody(error)) {
    throw new BackpackServiceError(error);
  }
  throw new Error(error);
};

export const useDeleteSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSite,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SITE_SUMMARY_KEY] });
      await queryClient.invalidateQueries({
        queryKey: [ACCOUNT_SITES_KEY],
      });
    },
  });
};
